import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import Avatar from "../Avatar";
import SceneObjectItem from "./SceneObjectItem";
import { getSceneObjectDimensions } from "./sceneObjectSizes";
import { AvatarDirection, AvatarPose, AvatarState } from "../../types/avatar";
import { useAuth } from "../AuthContext";
import {
  db,
  updateVirtualAvatar,
  deleteVirtualAvatar,
  updateSceneObject,
  deleteSceneObject,
} from "../../firebase";
import { SessionSettingsResponse } from "./types";
import {
  getSceneAvatarDimensions,
  type RoomLayoutMode,
} from "./mobileLayout";
import { readAvatarPosition } from "./roomRejoin";

interface SceneProps {
  roomId: string;
  meetingClient?: any | null;
  sessionSetting?: SessionSettingsResponse | null;
  onError?: (error: string) => void;
  layoutMode?: RoomLayoutMode;
}

interface ParticipantData {
  id: string;
  name: string;
  roleInSession: "representative" | "observer";
  avatarId: string | null;
  x?: number;
  y?: number;
  xRel?: number;
  yRel?: number;
  pose?: AvatarPose;
  direction?: AvatarDirection;
  role?: "terapeuta" | "paciente" | "participante";
}

interface VirtualAvatarData {
  id: string;
  name: string;
  avatarId: string | null;
  xRel?: number;
  yRel?: number;
  pose?: AvatarPose;
  direction?: AvatarDirection;
  createdBy?: string;
  createdByName?: string | null;
}

interface SceneObjectData {
  id: string;
  objectId: string;
  name?: string;
  xRel?: number;
  yRel?: number;
  createdBy?: string;
  createdByName?: string | null;
}

type SceneAvatar = AvatarState & { isVirtual: boolean };

const Scene: React.FC<SceneProps> = ({ roomId, sessionSetting, onError, layoutMode }) => {
  const { user } = useAuth();
  const sceneRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);
  const lastMoveTime = useRef<number>(0);
  const lastObjectMoveTime = useRef<number>(0);

  const [outerSize, setOuterSize] = useState({ width: 0, height: 0 });
  const [sceneSize, setSceneSize] = useState({ width: 0, height: 0 });
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [virtualAvatars, setVirtualAvatars] = useState<VirtualAvatarData[]>([]);
  const [sceneObjects, setSceneObjects] = useState<SceneObjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInitialPositionSet, setIsInitialPositionSet] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState("");

  const debugLog = useCallback((message: string, data?: any) => {
    if (process.env.NODE_ENV === "development") {
      console.log(`🎬 Scene: ${message}`, data || "");
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const pickBackground = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isDesktop = layoutMode === "desktop" && w >= 1024;
      if (isDesktop) {
        setBackgroundImage("/assets/fundo2.jpg");
        return;
      }
      // Celular em retrato: cenário vertical com o círculo em foco.
      // Celular em paisagem: usar o fundo largo do desktop (fundo2.jpg), pois a
      // imagem quadrada (fundo.png) era cortada no topo/base em telas horizontais,
      // escondendo o círculo do chão.
      const isPortrait = h >= w;
      setBackgroundImage(isPortrait ? "/assets/fundo-retrato.jpg" : "/assets/fundo2.jpg");
    };

    pickBackground();
    window.addEventListener("resize", pickBackground);
    window.addEventListener("orientationchange", pickBackground);

    return () => {
      window.removeEventListener("resize", pickBackground);
      window.removeEventListener("orientationchange", pickBackground);
    };
  }, [layoutMode]);

  // Mede o container disponível (não a cena), para então ajustar a cena.
  useEffect(() => {
    const updateOuterSize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setOuterSize({ width: rect.width, height: rect.height });
      }
    };

    updateOuterSize();
    const timeoutId = setTimeout(updateOuterSize, 100);

    const resizeObserver = new ResizeObserver(updateOuterSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [loading, backgroundImage]);

  const getSceneDimensions = useCallback(() => {
    if (sceneSize.width > 0 && sceneSize.height > 0) {
      return sceneSize;
    }
    if (sceneRef.current) {
      const rect = sceneRef.current.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return { width: rect.width, height: rect.height };
      }
    }
    return { width: window.innerWidth, height: window.innerHeight };
  }, [sceneSize]);

  const calculateRelativePositioning = useCallback((dimensions: { width: number; height: number }) => {
    const { width: avatarWidth, height: avatarHeight } = getSceneAvatarDimensions(dimensions);

    const navigableWidth = Math.max(1, dimensions.width - avatarWidth);
    const navigableHeight = Math.max(1, dimensions.height - avatarHeight);

    return { navigableWidth, navigableHeight };
  }, []);

  const relativeToAbsolute = useCallback(
    (xRel: number, yRel: number) => {
      const dimensions = getSceneDimensions();
      const { navigableWidth, navigableHeight } = calculateRelativePositioning(dimensions);
      return {
        x: Math.round(xRel * navigableWidth),
        y: Math.round(yRel * navigableHeight),
      };
    },
    [getSceneDimensions, calculateRelativePositioning]
  );

  const computeAbsolutePosition = useCallback(
    (xRel?: number, yRel?: number) => {
      if (typeof xRel !== "number" || typeof yRel !== "number") {
        return { x: 0, y: 0 };
      }
      return relativeToAbsolute(xRel, yRel);
    },
    [relativeToAbsolute]
  );

  const absoluteToRelative = useCallback(
    (x: number, y: number) => {
      const dimensions = getSceneDimensions();
      const { navigableWidth, navigableHeight } = calculateRelativePositioning(dimensions);
      return {
        xRel: Math.max(0, Math.min(1, x / navigableWidth)),
        yRel: Math.max(0, Math.min(1, y / navigableHeight)),
      };
    },
    [getSceneDimensions, calculateRelativePositioning]
  );

  const calculateObjectRelativePositioning = useCallback((
    dimensions: { width: number; height: number },
    objectId: string
  ) => {
    const isCompactScene =
      dimensions.width < 768 ||
      (dimensions.height > dimensions.width && dimensions.width < 1024);
    const { width: objectWidth, height: objectHeight } = getSceneObjectDimensions(objectId, isCompactScene);
    return {
      navigableWidth: Math.max(1, dimensions.width - objectWidth),
      navigableHeight: Math.max(1, dimensions.height - objectHeight),
    };
  }, []);

  const objectRelativeToAbsolute = useCallback(
    (xRel: number, yRel: number, objectId: string) => {
      const dimensions = getSceneDimensions();
      const { navigableWidth, navigableHeight } = calculateObjectRelativePositioning(dimensions, objectId);
      return {
        x: Math.round(xRel * navigableWidth),
        y: Math.round(yRel * navigableHeight),
      };
    },
    [getSceneDimensions, calculateObjectRelativePositioning]
  );

  const computeObjectAbsolutePosition = useCallback(
    (xRel: number | undefined, yRel: number | undefined, objectId: string) => {
      if (typeof xRel !== "number" || typeof yRel !== "number") {
        return { x: 0, y: 0 };
      }
      return objectRelativeToAbsolute(xRel, yRel, objectId);
    },
    [objectRelativeToAbsolute]
  );

  const objectAbsoluteToRelative = useCallback(
    (x: number, y: number, objectId: string) => {
      const dimensions = getSceneDimensions();
      const { navigableWidth, navigableHeight } = calculateObjectRelativePositioning(dimensions, objectId);
      return {
        xRel: Math.max(0, Math.min(1, x / navigableWidth)),
        yRel: Math.max(0, Math.min(1, y / navigableHeight)),
      };
    },
    [getSceneDimensions, calculateObjectRelativePositioning]
  );

  const updateParticipantData = useCallback(
    async (userId: string, dataToUpdate: object) => {
      if (!roomId || !userId) return;
      try {
        const participantDocRef = doc(db, "rooms", roomId, "participants", userId);
        await updateDoc(participantDocRef, dataToUpdate);
      } catch (err) {
        console.error(`Error updating participant ${userId}:`, err);
        if (mountedRef.current) {
          onError?.("Erro ao sincronizar alteração.");
        }
      }
    },
    [roomId, onError]
  );

  useEffect(() => {
    if (!roomId) return;

    setLoading(true);
    const participantsCollectionRef = collection(db, "rooms", roomId, "participants");
    const unsubscribe = onSnapshot(
      participantsCollectionRef,
      (snapshot) => {
        if (!mountedRef.current) return;
        const participantsData: ParticipantData[] = snapshot.docs.map((docSnapshot) => {
          return { id: docSnapshot.id, ...docSnapshot.data() } as ParticipantData;
        });
        setParticipants(participantsData);
        setLoading(false);
      },
      (err) => {
        console.error("Scene Firestore listener error:", err);
        if (mountedRef.current) {
          setLoading(false);
          onError?.("Não foi possível sincronizar com a sala.");
        }
      }
    );

    return () => unsubscribe();
  }, [roomId, onError]);

  useEffect(() => {
    if (!roomId) return;

    const virtualAvatarsCollectionRef = collection(db, "rooms", roomId, "virtualAvatars");
    const unsubscribe = onSnapshot(
      virtualAvatarsCollectionRef,
      (snapshot) => {
        if (!mountedRef.current) return;
        const virtualData: VirtualAvatarData[] = snapshot.docs.map((docSnapshot) => {
          return { id: docSnapshot.id, ...docSnapshot.data() } as VirtualAvatarData;
        });
        setVirtualAvatars(virtualData);
      },
      (err) => {
        console.error("Scene Firestore virtual avatars listener error:", err);
        if (mountedRef.current) {
          onError?.("Não foi possível sincronizar avatares adicionais.");
        }
      }
    );

    return () => unsubscribe();
  }, [roomId, onError]);

  useEffect(() => {
    if (!roomId) return;

    const sceneObjectsRef = collection(db, "rooms", roomId, "sceneObjects");
    const unsubscribe = onSnapshot(
      sceneObjectsRef,
      (snapshot) => {
        if (!mountedRef.current) return;
        const objectsData: SceneObjectData[] = snapshot.docs.map((docSnapshot) => {
          return { id: docSnapshot.id, ...docSnapshot.data() } as SceneObjectData;
        });
        setSceneObjects(objectsData);
      },
      (err) => {
        console.error("Scene Firestore scene objects listener error:", err);
      }
    );

    return () => unsubscribe();
  }, [roomId]);

  useEffect(() => {
    if (!user || participants.length === 0 || sceneSize.width <= 0 || isInitialPositionSet) return;

    const currentUserData = participants.find((p) => p.id === user.uid);
    if (!currentUserData) return;

    if (currentUserData.xRel === undefined && currentUserData.yRel === undefined) {
      // Reentrada após queda: se o doc foi recriado do zero, restauramos a
      // ÚLTIMA posição conhecida (salva localmente) para o usuário "voltar como
      // estava". Só caímos no random quando não há posição salva (1ª entrada).
      const saved = readAvatarPosition(roomId, user.uid);
      if (saved) {
        debugLog(`Restoring saved position for user ${user.uid}`);
        const restore: Record<string, number | string> = {
          xRel: Math.max(0.05, Math.min(0.95, saved.xRel)),
          yRel: Math.max(0.05, Math.min(0.95, saved.yRel)),
        };
        if (saved.pose) restore.pose = saved.pose;
        if (saved.direction) restore.direction = saved.direction;
        updateParticipantData(user.uid, restore);
      } else {
        debugLog(`Setting initial position for user ${user.uid}`);
        const baseXRel = 0.4 + Math.random() * 0.2;
        const baseYRel = 0.4 + Math.random() * 0.2;
        updateParticipantData(user.uid, {
          xRel: Math.max(0.05, Math.min(0.95, baseXRel)),
          yRel: Math.max(0.05, Math.min(0.95, baseYRel)),
        });
      }
    }
    setIsInitialPositionSet(true);
  }, [user, participants, sceneSize, isInitialPositionSet, updateParticipantData, debugLog, roomId]);

  const virtualAvatarIds = useMemo(() => new Set(virtualAvatars.map((virtualItem) => virtualItem.id)), [virtualAvatars]);

  const avatars = useMemo<SceneAvatar[]>(() => {
    if (sceneSize.width === 0 || sceneSize.height === 0) return [];

    const participantAvatars: SceneAvatar[] = participants
      .filter((participant) => participant.roleInSession === "representative" && !!participant.avatarId)
      .map((participant) => {
        const { x, y } = computeAbsolutePosition(participant.xRel, participant.yRel);
        return {
          id: participant.id,
          username: participant.name,
          avatarId: participant.avatarId!,
          pose: participant.pose || "standing",
          direction: participant.direction || "front",
          x,
          y,
          isCurrentUser: user?.uid === participant.id,
          role: participant.role,
          isVirtual: false,
        };
      });

    const extraAvatars: SceneAvatar[] = virtualAvatars
      .filter((v) => !!v.avatarId)
      .map((virtualAvatar) => {
        const { x, y } = computeAbsolutePosition(virtualAvatar.xRel, virtualAvatar.yRel);
        return {
          id: virtualAvatar.id,
          username: virtualAvatar.name || "Avatar",
          avatarId: virtualAvatar.avatarId!,
          pose: virtualAvatar.pose || "standing",
          direction: virtualAvatar.direction || "front",
          x,
          y,
          isCurrentUser: false,
          role: "participante",
          isVirtual: true,
        };
      });

    return [
      ...participantAvatars.sort((a, b) => a.username.localeCompare(b.username)),
      ...extraAvatars.sort((a, b) => a.username.localeCompare(b.username)),
    ];
  }, [participants, virtualAvatars, sceneSize, user, computeAbsolutePosition]);

  const canControlAvatar = useCallback(
    (avatarId: string): boolean => {
      if (!user) return false;
      if (virtualAvatarIds.has(avatarId)) {
        return sessionSetting?.isRoomModerator ?? false;
      }
      if (sessionSetting?.isRoomModerator) return true;
      return user.uid === avatarId;
    },
    [user, sessionSetting, virtualAvatarIds]
  );

  const handleAvatarMove = useCallback(
    async (id: string, x: number, y: number) => {
      if (!canControlAvatar(id) || sceneSize.width === 0) return;

      const now = Date.now();
      if (now - lastMoveTime.current < 50) return;
      lastMoveTime.current = now;

      const { xRel, yRel } = absoluteToRelative(x, y);
      const controlMetadata = user
        ? { lastControlledBy: user.uid, lastControlledByName: user.name || user.email || null }
        : {};

      if (virtualAvatarIds.has(id)) {
        await updateVirtualAvatar(roomId, id, { xRel, yRel, ...controlMetadata });
      } else {
        await updateParticipantData(id, { xRel, yRel, ...controlMetadata });
      }
    },
    [canControlAvatar, sceneSize.width, absoluteToRelative, user, virtualAvatarIds, roomId, updateParticipantData]
  );

  const handleAvatarDelete = useCallback(
    async (id: string) => {
      if (!canControlAvatar(id) || !virtualAvatarIds.has(id)) return;
      try {
        await deleteVirtualAvatar(roomId, id);
      } catch (error) {
        console.error("Failed to delete virtual avatar", error);
        onError?.("Não foi possível remover o avatar virtual. Tente novamente.");
      }
    },
    [canControlAvatar, virtualAvatarIds, roomId, onError]
  );

  const handleObjectMove = useCallback(
    async (id: string, x: number, y: number) => {
      if (!sessionSetting?.isRoomModerator || sceneSize.width === 0) return;

      const now = Date.now();
      if (now - lastObjectMoveTime.current < 50) return;
      lastObjectMoveTime.current = now;

      const object = sceneObjects.find((item) => item.id === id);
      if (!object) return;

      const { xRel, yRel } = objectAbsoluteToRelative(x, y, object.objectId);
      await updateSceneObject(roomId, id, { xRel, yRel });
    },
    [sessionSetting?.isRoomModerator, sceneSize.width, objectAbsoluteToRelative, roomId, sceneObjects]
  );

  const handleObjectDelete = useCallback(
    async (id: string) => {
      if (!sessionSetting?.isRoomModerator) return;
      try {
        await deleteSceneObject(roomId, id);
      } catch (error) {
        console.error("Failed to delete scene object", error);
        onError?.("Não foi possível remover o objeto. Tente novamente.");
      }
    },
    [sessionSetting?.isRoomModerator, roomId, onError]
  );

  const handleAvatarPoseChange = useCallback(
    async (id: string, pose: AvatarPose) => {
      if (!canControlAvatar(id)) return;

      const controlMetadata = user
        ? { lastControlledBy: user.uid, lastControlledByName: user.name || user.email || null }
        : {};

      if (virtualAvatarIds.has(id)) {
        await updateVirtualAvatar(roomId, id, { pose, ...controlMetadata });
      } else {
        await updateParticipantData(id, { pose, ...controlMetadata });
      }
    },
    [canControlAvatar, user, virtualAvatarIds, roomId, updateParticipantData]
  );

  const handleAvatarDirectionChange = useCallback(
    async (id: string, direction: AvatarDirection) => {
      if (!canControlAvatar(id)) return;

      const controlMetadata = user
        ? { lastControlledBy: user.uid, lastControlledByName: user.name || user.email || null }
        : {};

      if (virtualAvatarIds.has(id)) {
        await updateVirtualAvatar(roomId, id, { direction, ...controlMetadata });
      } else {
        await updateParticipantData(id, { direction, ...controlMetadata });
      }
    },
    [canControlAvatar, user, virtualAvatarIds, roomId, updateParticipantData]
  );

  // A cena ocupa todo o espaço disponível. O cenário (imagem de fundo) é
  // escolhido conforme a orientação — em retrato usamos um fundo vertical com o
  // círculo em foco — e exibido com `cover`, sem barras vazias e sem deixar o
  // círculo pequeno.
  useEffect(() => {
    if (outerSize.width === 0 || outerSize.height === 0) return;
    setSceneSize((prev) =>
      prev.width === outerSize.width && prev.height === outerSize.height
        ? prev
        : { width: outerSize.width, height: outerSize.height }
    );
  }, [outerSize.width, outerSize.height]);

  if (loading || outerSize.width === 0 || sceneSize.width === 0 || !backgroundImage) {
    return (
      <div ref={containerRef} className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2" />
          <p className="text-blue-700">Carregando Cenário...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: "#dcd4c8" }}
    >
      <div
        ref={sceneRef}
        className="scene-container relative overflow-hidden"
        style={{
          width: sceneSize.width,
          height: sceneSize.height,
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Os avatares vêm do Firestore (fonte da verdade) e NÃO dependem da
            conexão de vídeo (meetingClient). Antes ficavam travados por
            `!!meetingClient`, então sumiam quando o app voltava do segundo
            plano e o cliente de vídeo se reiniciava. */}
        {avatars.map((avatar) => (
            <Avatar
              key={avatar.id}
              avatarId={avatar.id}
              username={avatar.username}
              avatarImageId={avatar.avatarId}
              pose={avatar.pose}
              direction={avatar.direction}
              x={avatar.x}
              y={avatar.y}
              isCurrentUser={avatar.isCurrentUser}
              canControl={canControlAvatar(avatar.id)}
              onDrag={handleAvatarMove}
              onPoseChange={handleAvatarPoseChange}
              onDirectionChange={handleAvatarDirectionChange}
              isVirtual={avatar.isVirtual}
              onDelete={handleAvatarDelete}
              sceneSize={sceneSize}
            />
          ))}

        {sceneObjects.map((obj) => {
          const { x, y } = computeObjectAbsolutePosition(obj.xRel, obj.yRel, obj.objectId);
          return (
            <SceneObjectItem
              key={obj.id}
              id={obj.id}
              objectId={obj.objectId}
              name={obj.name}
              x={x}
              y={y}
              canControl={sessionSetting?.isRoomModerator ?? false}
              sceneSize={sceneSize}
              onDrag={handleObjectMove}
              onDelete={handleObjectDelete}
            />
          );
        })}
      </div>
    </div>
  );
};

export default Scene;
